import { h } from 'preact';
import React, { useContext } from 'react';
import nacl from 'tweetnacl'
import naclUtil from 'tweetnacl-util'

import { DefaultLanguageContext } from './contexts';
import { MMS_ORIGIN, CCPA_ORIGIN, USNAT_ORIGIN, CMPV2_ORIGIN, SIGNATURE_PUBLIC_KEY, portalOrigins, USNAT_ORIGIN_BUILDER } from './constants';

export const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const getFirstOfType = (type, components = []) => {
  let target = null;
  for (let i = 0; i < components.length; i++) {
    if (components[i].type === type) return components[i];
    else if (!target) target = getFirstOfType(type, components[i].children);
  };
  return target;
};

export const getById = (id, components = [], iteration = 0) => {
  let target = null;
  for (let i = 0; i < components.length; i++) {
    if (components[i].id === id) return components[i];
    else if (!target) target = getById(id, components[i].children, iteration + 1);
  };
  return target;
};

export const deepClone = (item) => {
  if (item instanceof Array) return item.map(i => deepClone(i));
  else if (typeof item === 'object' && item !== null) {
    return Object.entries(item).reduce((newObj, [key, value]) => ({
      ...newObj,
      [key]: deepClone(value)
    }), {})
  }
  else return item;
};

export const formatPhoneNumber = (phoneNumber) => phoneNumber ? phoneNumber.length === 10 ? `${'+1'}${phoneNumber}` : `${'+'}${phoneNumber}` : '';

export const validatePhone = (phoneNumber) => phoneNumber ? /^\d{10}$/.test(phoneNumber.trim()) || /^\d{11}$/.test(phoneNumber.trim()) : true;
export const validateSsn = (ssn) => ssn ? /^\d{4}$/.test(ssn.trim()) : true;
export const validateEmail = (email) => /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(email);
export const addStylesToHead = (styles) => {
  return new Promise((resolve) => {
    const previous = document.getElementById('custom-styles');
    if (previous) previous.remove();
    const node = document.createElement('style');
    node.setAttribute('id', 'custom-styles');
    node.innerHTML = styles;
    // TODO - make sure these will cover all cases
    // node.onload = () => { 
    //   resolve();
    // }
    // node.onerror = () => {
    //   resolve();
    // }
    document.body.appendChild(node);
    resolve();
  });
}
export const convertClassName = (name) => {
  if (!name) return '';
  return ' ' + name.replace('.', '').replace('#', '');
}

export const getBrowserLanguage = () => {
  let language = (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    navigator.userLanguage;

  language = language && language.length >= 2 ? language.substring(0, 2).toUpperCase() : 'EN';
  const isNorwegian = ['NO', 'NB', 'NN'].includes(language);

  //following line is for iOS. we set norwegian to 'NO' in portal and browsers use 'NO'. iOS uses 'NB' so we must change it to 'NO'
  return isNorwegian ? 'NO' : language;
}

export const fieldsAreFilled = (formFields) => Object.values(formFields)
  .reduce((isFilled, field) => {
    field.error = field.required && field.value === '';
    return !field.error && isFilled
  }, true);

export const fieldsAreFilledValidly = (formFields) => Object.values(formFields)
  .reduce((isFilledValidly, field) => {
    field.error = field.mustBeVal !== null ? field.value !== field.mustBeVal : false;
    return !field.error && isFilledValidly
  }, true);

export const makeQueryParams = (keyPairs) => Object.entries(keyPairs)
  .reduce((queryParams, [key, value]) => `${queryParams}&${key}=${value}`, '?');

export const makeText = (isDev, settings, key, language) => {
  if (isDev) {
    return (settings.languages[settings.selectedLanguage] && settings.languages[settings.selectedLanguage][key])
      ? settings.languages[settings.selectedLanguage][key]
      : (settings.languages[language] && settings.languages[language][key])
        ? settings.languages[language][key]
        : settings[key];
  } else {
    return settings.languages[language] && settings.languages[language][key] || settings[key];
  };
};

export const languageUsed = (languageToUse, settings, key) => {
  return (settings.languages[languageToUse] && (settings.languages[languageToUse][key] !== undefined))
    ? languageToUse
    : useContext(DefaultLanguageContext);
}

export const formatDescription = (description = '') => {
  const headerAndBullets = description
    .split(/\r\n|\r|\n/)
    .map((line, i, lines) => {
      if (!line) {
        return
      };

      if (line.includes('<')) {
        line = line.replace(/(<([^>]+)>)/ig, '');
      }

      return (line[0] === '*' && i !== 0)
        ? (

          <li>
            {line.slice(1)}
          </li>

        )
        : (
          <div>
            {i !== 0 && <br />}
            <div>{line[0] === '*' ? line.slice(1) : line}</div>
            {(i !== lines.length - 1) && <br />}
          </div>
        );
    })

  return description
    ? headerAndBullets.reduce((acc, curr, i) => {
      if (curr && curr.type === 'div') {
        const sliced = headerAndBullets.slice(i + 1)
        const until = sliced.findIndex((el) => el && el.type === 'div')
        acc = [
          ...acc,
          curr,
          (
            <ul class="description-bullet">
              {sliced.slice(0, until === -1 ? sliced.length + 1 : until)}
            </ul>
          )
        ]
      }
      return acc
    }, [])
    : ''
}

const isLangInJSON = (targetLang, components = []) => {
  let found = false;
  for (let i = 0; i < components.length; i++) {
    const { settings, children } = components[i];
    if (settings && settings.languages && settings.languages[targetLang] && settings.languages[targetLang].text) {
      if (settings.languages[targetLang].text !== "<p><br></p>") {
        return true;
      };
    } else if (!found) {
      found = isLangInJSON(targetLang, children);
    }
  };
  return found;
}

export const isLangSupported = (languageSelected, settings, components) => {
  if (settings.supportedLanguages && settings.supportedLanguages.length) {
    return settings.supportedLanguages.includes(languageSelected);
  };

  if (settings.language || settings.defaultLanguage) {
    if (languageSelected === settings.language || languageSelected === settings.defaultLanguage) {
      return true;
    };
  };

  return isLangInJSON(languageSelected, components);
}

export const getComponentAndIndex = (children) => (id) => {
  const component = children.find((c) => c.id === id)
  const index = children.findIndex((c) => c.id === id)

  if (!component) {
    throw Error(`Bad ID: No component with ID ${id}`)
  }

  return { component, path: JSON.stringify([0, index]) }
}

export const isActionAllowed = (key) => {
  // allows changing states on elements while pressing either Space key or Enter
  // in voiceover Space key is expected to activate buttons etc.
  return key === ' ' || key === 'Spacebar' || key === 'Enter' || key === undefined;
}

export const setFirstAndLastElementsOnTheModal = () => {
  const msgComponent = document.getElementsByClassName('focusable');
  const firstEl = msgComponent[0];
  const lastEl = msgComponent[msgComponent.length - 1];
  const firstElClass = 'first-focusable-el';
  const lastElClass = 'last-focusable-el';
  if (firstEl && !firstEl.className.includes(firstElClass)) {
    firstEl.className += ` ${firstElClass}`;
  };
  if (lastEl && !lastEl.className.includes(lastElClass)) {
    lastEl.className += ` ${lastElClass}`;
  };
  const last = document.getElementsByClassName(lastElClass);
  if (document.getElementsByClassName(firstElClass).length > 1) {
    document.getElementsByClassName(firstElClass)[0].focus();
  }
  last[0] && last[0].addEventListener("keydown", e => {
    const isRegularTab = e.code === 'Tab' && e.shiftKey === false
    const isRightArrow = e.code === 'ArrowRight'
    if (e.target.className.includes(lastElClass) && (isRightArrow || isRegularTab)) {
      document.getElementsByClassName(firstElClass)[0].focus();
      e.preventDefault(); //prevents skipping to the next tab
    };
  });
};

export const getLanguage = (settings, consentLanguage, noticeComponent) => {
  let language;
  if (!settings.useBrowserDefault && (settings.supportedLanguages && !settings.supportedLanguages.includes(getBrowserLanguage()))) {
    language = consentLanguage || settings.language || settings.defaultLanguage;
  } else {
    language = consentLanguage || getBrowserLanguage();
    const languageIsSupported = isLangSupported(language, settings, [noticeComponent]);
    language = languageIsSupported ? language : (settings.language || settings.defaultLanguage || 'EN');
  };
  return language
}

export const getIframeTitle = (settings, consentLanguage, component) => {
  const language = getLanguage(settings, consentLanguage, component);
  const defaultTitle = settings.iframeTitle && settings.iframeTitle.replace(/^\<p\>/, "").replace(/\<\/p\>$/, "") || 'SP Consent Message';
  return settings.languages && settings.languages[language] ? settings.languages[language].iframeTitle.replace(/^\<p\>/, "").replace(/\<\/p\>$/, "") : defaultTitle;
}

// Contents of this function pulled from: https://web.dev/cls/#measure-cls-in-javascript
export const observeCLS = () => {
  console.log("Observing CLS (Cumulative Layout Shift)")
  let clsValue = 0;
  let clsEntries = [];

  let sessionValue = 0;
  let sessionEntries = [];

  try {
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        // Only count layout shifts without recent user input.
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0];
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

          // If the entry occurred less than 1 second after the previous entry and
          // less than 5 seconds after the first entry in the session, include the
          // entry in the current session. Otherwise, start a new session.
          if (sessionValue &&
            entry.startTime - lastSessionEntry.startTime < 1000 &&
            entry.startTime - firstSessionEntry.startTime < 5000) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }

          // If the current session value is larger than the current CLS value,
          // update CLS and the entries contributing to it.
          if (sessionValue > clsValue) {
            clsValue = sessionValue;
            clsEntries = sessionEntries;

            // Log the updated value (and its entries) to the console.
            console.log('CLS:', clsValue, clsEntries)
          }
        }
      }
    }).observe({ type: 'layout-shift', buffered: true });

  } catch (err) { }
}

export const includesComponent = (typeToFind, component) => {
  return (component.type === typeToFind) || component.children.some(child => includesComponent(typeToFind, child))
}

export const getValidOrigin = (originType) => {
  let origin;
  let path;
  const originParam = getParameterByName(originType);

  if (originType === 'mms_origin') {
    origin = MMS_ORIGIN;
    path = 'mms/v2';
  } else if ((originType === 'consent_origin') || (originType === 'cmpv2_origin')) {
    origin = CMPV2_ORIGIN;
    path = 'consent/tcfv2';
  } else if (originType === 'ccpa_origin') {

    origin = CCPA_ORIGIN;
    path = null; // no path
  } else if (originType === 'usnat_origin') {
    origin = USNAT_ORIGIN;
    path = null; // no path
  } else if (originType === 'usnat_origin_builder') {
    origin = USNAT_ORIGIN_BUILDER;
    path = null; // no path
  }

  let expectedOriginParam = window.location && (`https://${window.location.hostname}`);
  
  if (path) {
    expectedOriginParam += `/${path}`
  }

  if (originParam && (originParam === expectedOriginParam || originParam === `${expectedOriginParam}/`)) {
    origin = originParam;
  }
  return origin;
}

export const verifyMsg = (msgJson, signature) => {
  if (!msgJson || !signature) return false;
  const pub64 = naclUtil.decodeBase64(SIGNATURE_PUBLIC_KEY)
  const uIntSignature = new Uint8Array(signature)

  const utf8Json = new Uint8Array(naclUtil.decodeUTF8(msgJson))

  return nacl.sign.detached.verify(utf8Json, uIntSignature, pub64)
}

export const isValidDevOrigin = ({ origin }) => {
  let currentPortalOrigins = [...portalOrigins];
  if (env && !env.PRODUCTION) {
    currentPortalOrigins.push('http://localhost:3000');
  }
  return currentPortalOrigins.includes(origin) || env && (env.DEV || env.NODE_ENV === 'test')
}